import React, { useEffect } from "react";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { LicenseInfo } from "@mui/x-license";
import { env } from "../../env.js";

LicenseInfo.setLicenseKey(env.MUI_LICENSE_KEY);

const DataGrid = (props) => {
  const { config, height, maxHeight, colors } = props;

  // Disable ResizeObserver loop errors by wrapping callbacks safely
  useEffect(() => {
    const Original = window.ResizeObserver as typeof ResizeObserver;
    const previous = window.ResizeObserver;

    class WrappedResizeObserver extends Original {
      constructor(callback: ResizeObserverCallback) {
        super((entries, observer) => {
          requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (error: any) {
              const msg = String(error?.message ?? "");
              if (
                msg.includes("ResizeObserver loop completed with undelivered notifications") ||
                msg.includes("ResizeObserver loop limit exceeded")
              ) {
                // Ignore ResizeObserver loop noise
                return;
              }
              throw error;
            }
          });
        });
      }
    }

    // Assign typed wrapper
    window.ResizeObserver = WrappedResizeObserver as unknown as typeof ResizeObserver;

    // Restore original on cleanup
    return () => {
      window.ResizeObserver = previous as unknown as typeof ResizeObserver;
    };
  }, []);

  //@ts-nocheck @ts-ignore
  return (
    <div
      style={{
        height: height !== undefined ? `${height}` : `70vh`,
        maxHeight: maxHeight ? `${maxHeight}` : "",
        width: "100%",
        border: "none",
        outline: "none",
      }}
    >
      <DataGridPro
        sx={{
          // Base DataGrid styling from StyledDataGrid
          border: "none !important",
          outline: "none !important",
          "& .MuiDataGrid-root": {
            backgroundColor: "none",
            border: "none !important",
            borderWidth: "0px !important",
            borderStyle: "none !important",
            borderColor: "transparent !important",
            outline: "none !important",
          },

          // Cell styling
          "& .MuiDataGrid-cell": {
            color: colors?.text?.label,
            fontWeight: 400,
            fontSize: "1rem",
            fontFamily: "Roboto",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            border: "none",
            minHeight: "3rem !important",
            lineHeight: "1.5rem", 
            padding: "0.25rem 1.5rem 0.25rem 1rem",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },

          "& .MuiDataGrid-columnHeader": {
            backgroundColor: colors?.background?.paper,
            color: colors?.text?.secondary,
            display: "flex",
            border: "none !important",
            borderBottom: "none !important",
            borderTop: "none !important",
            borderLeft: "none !important",
            borderRight: "none !important",
            minHeight: "3rem !important", 
            padding: "0.25rem 1.5rem 0.25rem 1rem", 
          },
          // Ensure headers container isn't transparent during mount
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors?.background?.paper,
          },

          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 500,
            lineHeight: "1.5rem",
            fontSize: "1rem",
            fontFamily: "Roboto",
          },

          "& .MuiDataGrid-columnHeaderTitleContainer": {
            borderBottom: "none !important",
            border: "none !important",
            display: "flex",
            justifyContent: "flex-start !important",
            alignItems: "center",
          },

          // Additional header border removal
          "& .MuiDataGrid-columnHeaderRow": {
            borderBottom: "none !important",
            border: "none !important",
          },

          // Row styling
          "& .MuiDataGrid-row": {
            backgroundColor: colors?.background?.paper,
            border: "none",
            borderBottom: `0.5px solid ${colors?.border?.card} !important`,
          },

          // Pinned left cells
          "& .MuiDataGrid-cell--pinnedLeft": {
            backgroundColor: `${colors?.background?.paper} !important`,
            border: "none",
            color: colors?.text?.secondary,
            zIndex: 100,
          },

          "& .MuiDataGrid-columnHeader--pinnedLeft": {
            backgroundColor: `${colors?.background?.paper} !important`,
            border: "none",
            zIndex: 101,

          },

          // Fix header checkbox positioning and width
          "& .MuiDataGrid-columnHeader[data-field='__check__']": {
            left: "0 !important",
            minWidth: "70px !important",
            width: "70px !important",
          },

          // Hover row
          "& .MuiDataGrid-row:hover": {
            backgroundColor: colors?.hover?.muted,
          },

          // Selected row
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: colors?.primary?.selected,
          },

          // Selected + Hover row
          "& .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: colors?.hover?.primary,
          },

          // Checkbox colors
          "& .MuiCheckbox-root": {
            color: colors?.secondary?.main,
          },
          "& .MuiCheckbox-root.Mui-checked": {
            color: colors?.primary?.main,
          },

          // Detail panel toggle chevron
          "& .MuiDataGrid-detailPanelToggleCell": {
            "& .MuiIconButton-root": {
              color: `${colors?.text?.muted} !important`,
            },
            "& .MuiSvgIcon-root": {
              color: `${colors?.text?.muted} !important`,
            },
          },

          // Detail panel
          "& .MuiDataGrid-detailPanel": {
            backgroundColor: colors?.background?.accent,
            borderBottomLeftRadius: "1rem",
            borderBottomRightRadius: "1rem",
          },
          "& .MuiDataGrid-row--borderBottom .MuiDataGrid-filler": {
            backgroundColor: colors?.background?.paper,
          },
          "& .MuiDataGrid-filler": {
            backgroundColor: colors?.background?.paper,
            border: "none !important",
          },
          "& .MuiDataGrid-filler > div": {
            borderTop: "none !important",
          },
          "& .MuiDataGrid-filler--pinnedLeft": {
            borderRight: "none !important",
            backgroundColor: "transparent",
            borderTop: "none !important",
          },
          "& .MuiDataGrid-virtualScrollerContent": {
            "--DataGrid-overlayHeight": "100% !important",
          },

          // Sort icons (up/down arrows)
          "& .MuiDataGrid-sortIcon": {
            color: colors?.text?.muted,
          },

          // Column menu (three dots icon)
          "& .MuiDataGrid-menuIconButton": {
            color: colors?.text?.muted,
            "& .MuiSvgIcon-root": {
              color: colors?.text?.muted,
            },
          },

          // Column separator
          "& .MuiDataGrid-columnSeparator": {
            display: "none", // Hide column separator
          },

          // Footer
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none !important",
            border: "none !important",
            backgroundColor: colors?.background?.paper,
          },

          /* Rows per page: label, value, and "x–y of z" text */
          "& .MuiTablePagination-root, & .MuiTablePagination-toolbar": {
            color: colors?.text?.secondary,
          },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-caption": {
            color: colors?.text?.secondary,
            fontSize: "0.875rem",
          },

          /* The select that controls rows per page */
          "& .MuiTablePagination-select, & .MuiTablePagination-input": {
            color: colors?.text?.secondary,
          },
          "& .MuiTablePagination-select .MuiSelect-select": {
            backgroundColor: "transparent",
          },
          "& .MuiTablePagination-root .MuiSelect-nativeInput ~ .MuiSelect-icon": {
            color: `${colors?.text?.muted} !important`,
          },

          /* Left/right pagination arrows */
          "& .MuiTablePagination-actions .MuiIconButton-root": {
            color: colors?.text?.muted,
            "& .MuiSvgIcon-root": { color: "inherit" },
            "&:hover": { color: colors?.primary?.main },
            "&.Mui-disabled": { color: colors?.text?.disabled },
          },

          // Additional border removal for all possible containers
          "& .MuiDataGrid-main": {
            border: "none !important",
            outline: "none !important",
          },
          "& .MuiDataGrid-container": {
            border: "none !important",
            outline: "none !important",
          },
          "& .MuiDataGrid-virtualScroller": {
            border: "none !important",
            outline: "none !important",
          },
          ...(config?.customSx || {}),
        }}
        {...config}
      />
    </div>
  );
};

export default DataGrid;
